import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { FluentProvider, IdPrefixProvider } from '@fluentui/react-components';
import { AADocument } from '@55degrees/aa-core-ts-data-formats';
import { reportMixPanel, setupMixPanel } from '@55degrees/consent-tracker';
import { Generator } from '@55degrees/aa-data-examples';
import { Route, Routes } from 'react-router';
import LegacyUtils from './components/legacy/LegacyUtils';
import { HistoryDataFetchLimit, WeekStartsOnDay } from './config';
import { calculateFooterHeight, getTranslation, sessionMixPanelData } from './helper';
import { ProductFruitsInit } from './components/Helpers/ProductFruitsInit';
import useAppTheme from './hooks/useAppTheme';
import AzureRouter from './utilities/AzureRouter';
import A3DocumentContextProvider from './contexts/A3DocumentContext';
import Footer from './components/Footer';
import LicenseValidator from './utilities/LicenseValidator';
import useSubscription from './hooks/useSubscription';
import { AppContext } from './globals/AppContext';
import getHashedUserId from './components/Helpers/hashAccountHelper';
import packageJSON from '../package.json';
import Header from './components/Header';
import { DateFormat, Filters, Locale, ResetFiltersScope } from './types/A3';
import TrialBanner from './components/Footer/TrialBanner';
import { AppFooter } from './components/Footer/styling';
import { queryClient } from './setupTanstackQuery';
import { DataLoaderStartOptions, DataLoaderStartView, PageState } from './types';
import PageSwitcher from './utilities/PageSwitcher';
import RedeemAccessToken from './components/RedeemAccessToken';
import ActionableAgile from './components/ActionableAgile';
import UrlChecker from './utilities/UrlChecker';
import ExternalUrlChecker from './utilities/ExternalUrlChecker';
import useAppSettingsQuery from './hooks/useAppSettings';
import useSavedDatasets from './hooks/useSavedDatasets';
import { StartupMode } from './types/AppSettings';
import MixPanelContext from './contexts/MixPanelContext';
import LoadingPage from './pages/LoadingPage';
import './components/overrideStyling.css';
import { APP_HEADER_HEIGHT } from './config';
import LandingPageWrapper from './pages/landing-page/LandingPageWrapper';
import AutoLoadMarker from './utilities/AutoLoadMarker';
import QueryErrorHandler from './components/ErrorBoundary/QueryErrorHandler';
import { DataLoaderContextProvider } from './contexts/DataLoaderContext';

// LAZY COMPONENTS
const BillingPortal = lazy(() => import('./components/Header/Dialogs/BillingPortal'));
const ManageUsers = lazy(() => import('./components/Header/Dialogs/ManageUsers'));
const BetaFeaturesPage = lazy(() => import('./pages/BetaFeaturesPage'));
const DataLoader = lazy(() => import('./pages/DataLoader'));
const BuyNowDialog = lazy(() => import('./components/purchase/BuyNowDialog'));
const FileLoaderWrapper = lazy(() => import('./components/FileLoaderWrapper'));

const VITE_MIX_API_KEY = import.meta.env.VITE_MIX_API_KEY;
const VITE_MAU_MIX_API_KEY = import.meta.env.VITE_MAU_MIX_API_KEY;
const packageName = packageJSON.name;

window.$ALLOW_KEY_BIND = true;

function App() {
  const { isDarkTheme, muiTheme, updateDarkTheme, webTheme } = useAppTheme();

  const [a3Document, setA3Document] = useState<AADocument | null>(Generator('Default'));
  const [isMixPanelInitialized, setIsMixPanelInitialized] = useState(false);
  const [filters, setFilters] = useState<Filters | {}>({});
  const [resetFiltersScope, setResetFiltersScope] = useState<ResetFiltersScope | null>(null);
  const [loadingCounter, setLoadingCounter] = useState<number>(0);

  // DATA LOADER
  const [dataLoaderStartView, setDataLoaderStartView] = useState<DataLoaderStartView>('create');
  const [dataLoaderStartOptions, setDataLoaderStartOptions] = useState<DataLoaderStartOptions | {}>(
    {}
  );
  const [dataLoaderLoadUuid, setDataLoaderLoadUuid] = useState('');
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // INTERNAL PAGES
  const [pageState, setPageState] = useState<PageState>('app');

  // DIALOG STATES
  const [isBuyNowDialogOpen, setIsBuyNowDialogOpen] = useState(false);
  const [isRedeemAccessTokenDialogOpen, setIsRedeemAccessTokenDialogOpen] = useState(false);
  const [isBillingDialogOpen, setIsBillingDialogOpen] = useState(false);
  const [isFileLoaderDialogOpen, setIsFileLoaderDialogOpen] = useState(false);
  const [isManageUsersDialogOpen, setIsManageUsersDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [isHeaderEnabled, setIsHeaderEnabled] = useState<boolean>(false);
  const legacyRef = useRef();

  const subscriptionQuery = useSubscription();
  const appSettingsQuery = useAppSettingsQuery();
  const { allStrippedDatasets } = useSavedDatasets();

  const isAnyQueryLoading = subscriptionQuery.isLoading || appSettingsQuery.isLoading;
  const isAnyQueryFailing = subscriptionQuery.isError || appSettingsQuery.isError;

  const isOnBetaPage = pageState === 'beta-page';
  const isOnDataLoaderPage = pageState === 'data-loader';

  const mixPanelValue = useMemo(() => {
    return { isMixPanelInitialized, setIsMixPanelInitialized };
  }, [isMixPanelInitialized]);

  const sessionDataRef = useRef<{
    dateFormat: DateFormat;
    locale: Locale;
    isDarkTheme: boolean;
    historicalDataFetchLimit: HistoryDataFetchLimit;
    weekStartsOn: WeekStartsOnDay;
    startupMode: StartupMode;
  } | null>(null);

  const favoriteCountRef = useRef(0);
  const isSessionStartSentRef = useRef<boolean>(false);
  const isSessionEndSentRef = useRef<boolean>(false);

  // Updates the ref with latest data used for unload.
  useEffect(() => {
    if (appSettingsQuery.isLoading || appSettingsQuery.isError || !appSettingsQuery?.data) {
      return undefined;
    }

    sessionDataRef.current = {
      dateFormat: appSettingsQuery.data.dateFormat,
      locale: appSettingsQuery.data.locale,
      isDarkTheme: isDarkTheme,
      historicalDataFetchLimit: appSettingsQuery.data.historicalDataFetchLimit,
      weekStartsOn: appSettingsQuery.data.weekStartsOn,
      startupMode: appSettingsQuery.data.startupMode,
    };

    return undefined;
  }, [appSettingsQuery, isDarkTheme]);

  useEffect(() => {
    if (!allStrippedDatasets) return undefined;

    favoriteCountRef.current = allStrippedDatasets.filter((dataset) => dataset.favorite).length;

    return undefined;
  }, [allStrippedDatasets]);

  useEffect(() => {
    if (!isMixPanelInitialized) return undefined;
    if (appSettingsQuery.isLoading || appSettingsQuery.isError || !appSettingsQuery.data) {
      return undefined;
    }
    if (isSessionStartSentRef.current) return undefined;

    isSessionStartSentRef.current = true;
    const { dateFormat, locale, weekStartsOn, startupMode, historicalDataFetchLimit } =
      appSettingsQuery.data;
    reportMixPanel(
      'Session Start',
      sessionMixPanelData(
        {
          dateFormat,
          locale,
          isDarkMode: isDarkTheme,
          weekStartsOn,
          startupMode,
          historicalDataFetchLimit,
        },
        'start'
      )
    );

    return undefined;
  }, [isMixPanelInitialized, appSettingsQuery, isDarkTheme]);

  useEffect(() => {
    const handleUnload = () => {
      if (isSessionEndSentRef.current) return;
      if (!sessionDataRef.current) return;

      isSessionEndSentRef.current = true;
      const {
        dateFormat,
        locale,
        isDarkTheme: isDark,
        weekStartsOn,
        historicalDataFetchLimit,
        startupMode,
      } = sessionDataRef.current;
      reportMixPanel(
        'Session End',
        sessionMixPanelData(
          {
            dateFormat,
            locale,
            isDarkMode: isDark,
            weekStartsOn,
            startupMode,
            historicalDataFetchLimit,
            favoriteCount: favoriteCountRef.current,
          },
          'end'
        )
      );
    };
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('unload', handleUnload);
    };
  }, []);

  useEffect(() => {
    if (
      subscriptionQuery.isLoading ||
      subscriptionQuery.isError ||
      !VITE_MIX_API_KEY ||
      !VITE_MAU_MIX_API_KEY
    ) {
      return;
    }
    if (isMixPanelInitialized) return;

    const { environment, isProduction } = AppContext;
    const {
      data: { has_user_access: hasUserAccess, recurlySubscriptionId, recurlyAccountCode },
    } = subscriptionQuery;
    const customerType = hasUserAccess ? 'Subscriber' : 'Evaluator';
    const storedConsent = JSON.parse(localStorage.getItem('55d-azure-user-tracking-consent'));
    const consent = storedConsent?.value?.isEnabled || false;
    const organizationAccount = VSS.getWebContext().user;
    const userAccount = VSS.getWebContext().user;
    const companyName = isProduction ? organizationAccount?.name : 'local-SEN';
    const appUrl = new URL(VSS.getWebContext().collection.uri);

    const hashedUserId = getHashedUserId(userAccount);

    const mixPanelProps = {
      initOptions: {
        debug: environment === 'local',
      },
      superProps: {
        'Package Name': packageName,
        Environment: environment,
        'Hosting Platform': 'Azure',
        'Entitlement Slug': recurlySubscriptionId,
        'Customer Type': customerType,
        $initial_referrer: appUrl.origin,
        $initial_referring_domain: appUrl.hostname,
        $referrer: appUrl.origin,
        $referring_domain: appUrl.hostname,
      },
      userProps: {
        $initial_referrer: appUrl.origin,
        $initial_referring_domain: appUrl.hostname,
      },
    };

    setupMixPanel(
      VITE_MIX_API_KEY,
      VITE_MAU_MIX_API_KEY,
      consent,
      hashedUserId,
      recurlyAccountCode,
      recurlySubscriptionId,
      companyName,
      mixPanelProps,
      'ActionableAgile Analytics for Azure DevOps'
    );
    setIsMixPanelInitialized(true);
    console.log('MixPanel has been initialized with consent:', consent);
  }, [subscriptionQuery, isMixPanelInitialized]);

  if (isAnyQueryLoading) {
    return (
      <ThemeProvider theme={muiTheme}>
        <LoadingPage />
      </ThemeProvider>
    );
  }

  if (isAnyQueryFailing) {
    return (
      <ThemeProvider theme={muiTheme}>
        <IntlProvider locale="en-US" messages={getTranslation('en-US')} defaultLocale="en-US">
          <QueryErrorHandler
            subscriptionQuery={subscriptionQuery}
            appSettingsQuery={appSettingsQuery}
          />
        </IntlProvider>
      </ThemeProvider>
    );
  }

  const {
    data: { dateFormat, historicalDataFetchLimit, locale, weekStartsOn },
  } = appSettingsQuery;

  const appFooterHeight = calculateFooterHeight(subscriptionQuery.data.isTrialActive);

  const enableHeader = () => {
    setIsHeaderEnabled(true);
  };

  const disableHeader = () => {
    setIsHeaderEnabled(false);
  };

  const openDataLoader = (
    view: DataLoaderStartView,
    options: DataLoaderStartOptions = { currentFilter: '' }
  ) => {
    disableHeader();
    setPageState('data-loader');
    setDataLoaderStartView(view);
    setDataLoaderStartOptions(options);
  };

  const closeDataLoader = () => {
    enableHeader();
    setPageState('app');
    setDataLoaderStartView('create');
    setDataLoaderStartOptions({});
    setDataLoaderLoadUuid('');

    const {
      account: { id: organizationId },
      project: { id: projectId },
    } = VSS.getWebContext();

    // @ts-ignore
    queryClient.invalidateQueries([`datasets-recent-${organizationId}-${projectId}`]);
  };

  const loadDatasetWithUuid = (selectedUuid: string) => {
    disableHeader();
    setPageState('data-loader');
    setDataLoaderStartView('create');
    setDataLoaderLoadUuid(selectedUuid);
  };

  const openBuyNowDialog = () => {
    setIsBuyNowDialogOpen(true);
  };

  const closeBuyNowDialog = () => {
    setIsBuyNowDialogOpen(false);
  };

  const openRedeemAccessTokenDialog = () => {
    setIsRedeemAccessTokenDialogOpen(true);
  };

  const closeRedeemAccessTokenDialog = () => {
    setIsRedeemAccessTokenDialogOpen(false);
  };

  const openManageUsersDialog = () => {
    setIsManageUsersDialogOpen(true);
  };

  const closeManageUsersDialog = () => {
    setIsManageUsersDialogOpen(false);
  };

  const openBillingDialog = () => {
    setIsBillingDialogOpen(true);
  };

  const closeBillingDialog = () => {
    setIsBillingDialogOpen(false);
  };

  const openFileLoaderDialog = () => {
    setIsFileLoaderDialogOpen(true);
  };

  const closeFileLoaderDialog = () => {
    setIsFileLoaderDialogOpen(false);
  };

  const openDeleteDialog = () => {
    setIsDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const onRedirectToBetaPage = () => {
    setPageState('beta-page');
  };

  const updateLoadingCounter = () => {
    setLoadingCounter((prev) => prev + 1);
  };

  const isAnyDialogOpen =
    isBuyNowDialogOpen ||
    isRedeemAccessTokenDialogOpen ||
    isBillingDialogOpen ||
    isFileLoaderDialogOpen ||
    isManageUsersDialogOpen ||
    isDeleteDialogOpen;

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline enableColorScheme />
      <IntlProvider locale={locale} messages={getTranslation(locale)} defaultLocale="en-US">
        <MixPanelContext.Provider value={mixPanelValue}>
          <DataLoaderContextProvider>
            <ProductFruitsInit isDarkTheme={isDarkTheme} locale={locale} />
            <AzureRouter>
              <A3DocumentContextProvider
                a3Document={a3Document}
                closeDataLoader={closeDataLoader}
                setA3Document={setA3Document}
                setIsFirstLoad={setIsFirstLoad}
                updateLoadingCounter={updateLoadingCounter}
                updateLastLoadedData={appSettingsQuery.updateLastLoadedDatasetId}
                dataLoaderStartOptions={dataLoaderStartOptions}
              >
                {/* HEADER */}
                <IdPrefixProvider value="HEADER-APP">
                  <FluentProvider theme={webTheme}>
                    {isHeaderEnabled && (
                      <Header
                        filters={filters}
                        setResetFiltersScope={setResetFiltersScope}
                        openBillingPortalDialog={openBillingDialog}
                        openManageUsersDialog={openManageUsersDialog}
                        loadingCounter={loadingCounter}
                        isDialogOpen={isAnyDialogOpen}
                        isDeleteDialogOpen={isDeleteDialogOpen}
                        redirectToBetaPage={onRedirectToBetaPage}
                        isBetaPage={isOnBetaPage}
                        setIsDarkMode={updateDarkTheme}
                        isDarkMode={isDarkTheme}
                        openDataLoader={openDataLoader}
                        loadDatasetFromDropdown={loadDatasetWithUuid}
                        isMixPanelInitialized={isMixPanelInitialized}
                        openDeleteDialog={openDeleteDialog}
                        closeDeleteDialog={closeDeleteDialog}
                        openFileLoader={openFileLoaderDialog}
                      />
                    )}
                  </FluentProvider>
                </IdPrefixProvider>
                <LicenseValidator
                  enableHeader={enableHeader}
                  disableHeader={disableHeader}
                  onBuyNowClick={openBuyNowDialog}
                  webTheme={webTheme}
                  pageState={pageState}
                >
                  <PageSwitcher
                    betaPageComponent={
                      <BetaFeaturesPage theme={webTheme} headerHeight={APP_HEADER_HEIGHT} />
                    }
                    dataLoaderComponent={
                      <DataLoader
                        dataLoaderLoadUuid={dataLoaderLoadUuid}
                        dataLoaderStartOptions={dataLoaderStartOptions}
                        dataLoaderStartView={dataLoaderStartView}
                        footerHeight={appFooterHeight + 37}
                        isDarkMode={isDarkTheme}
                        historicalDataFetchLimit={historicalDataFetchLimit}
                        locale={locale}
                        onGoBackClick={closeDataLoader}
                      />
                    }
                    isOnBetaPage={isOnBetaPage}
                    isOnDataLoaderPage={isOnDataLoaderPage}
                  >
                    <Routes>
                      <Route
                        index
                        element={
                          <LandingPageWrapper
                            loadDataset={loadDatasetWithUuid}
                            openDataLoader={openDataLoader}
                          />
                        }
                      />
                      <Route
                        path="/example"
                        element={
                          <AutoLoadMarker>
                            <ActionableAgile
                              key="actionable-agile-example-data"
                              appFooterHeight={appFooterHeight}
                              dateFormat={dateFormat}
                              weekStartsOn={weekStartsOn}
                              isDarkMode={isDarkTheme}
                              locale={locale}
                              notifyFiltering={setFilters}
                              resetFiltersScope={resetFiltersScope}
                            />
                          </AutoLoadMarker>
                        }
                      />
                      <Route
                        path="/external"
                        element={
                          <AutoLoadMarker>
                            <ExternalUrlChecker
                              isFirstLoad={isFirstLoad}
                              setIsFirstLoad={setIsFirstLoad}
                            >
                              <ActionableAgile
                                key={`actionable-agile-${a3Document?.title}`}
                                appFooterHeight={appFooterHeight}
                                dateFormat={dateFormat}
                                weekStartsOn={weekStartsOn}
                                isDarkMode={isDarkTheme}
                                locale={locale}
                                notifyFiltering={setFilters}
                                resetFiltersScope={resetFiltersScope}
                              />
                            </ExternalUrlChecker>
                          </AutoLoadMarker>
                        }
                      />
                      <Route
                        path="/:datasetId"
                        element={
                          <AutoLoadMarker>
                            <UrlChecker
                              loadDatasetFromUuid={loadDatasetWithUuid}
                              setIsFirstLoad={setIsFirstLoad}
                              isFirstLoad={isFirstLoad}
                            >
                              <ActionableAgile
                                key={`actionable-agile-${a3Document?.title}-${loadingCounter}`}
                                appFooterHeight={appFooterHeight}
                                dateFormat={dateFormat}
                                weekStartsOn={weekStartsOn}
                                isDarkMode={isDarkTheme}
                                locale={locale}
                                notifyFiltering={setFilters}
                                resetFiltersScope={resetFiltersScope}
                              />
                            </UrlChecker>
                          </AutoLoadMarker>
                        }
                      />
                    </Routes>
                  </PageSwitcher>
                </LicenseValidator>
                {/* FOOTER */}
                <AppFooter>
                  {subscriptionQuery.data?.isTrialActive && (
                    <TrialBanner
                      onBuyNowClick={openBuyNowDialog}
                      onRedeemTokenClick={openRedeemAccessTokenDialog}
                    />
                  )}
                  <IdPrefixProvider value="FOOTER-APP">
                    <FluentProvider theme={webTheme}>
                      <Footer isDarkMode={isDarkTheme} />
                    </FluentProvider>
                  </IdPrefixProvider>
                </AppFooter>
                {/* DIALOGS */}
                <IdPrefixProvider value="CONTENT-DIALOGS">
                  <FluentProvider theme={webTheme}>
                    {isBillingDialogOpen && (
                      <Suspense fallback={null}>
                        <BillingPortal
                          isOpen={isBillingDialogOpen}
                          closeDialog={closeBillingDialog}
                        />
                      </Suspense>
                    )}
                    {isManageUsersDialogOpen && (
                      <Suspense fallback={null}>
                        <ManageUsers
                          isOpen={isManageUsersDialogOpen}
                          closeDialog={closeManageUsersDialog}
                          subscription={subscriptionQuery.subscriptionClass}
                        />
                      </Suspense>
                    )}
                    {isFileLoaderDialogOpen ? (
                      <Suspense fallback={null}>
                        <FileLoaderWrapper
                          locale={locale}
                          isDarkMode={isDarkTheme}
                          fileFormatHelpLink="https://55d.se/file-format-azure"
                          isDialogOpen={isFileLoaderDialogOpen}
                          closeDialog={closeFileLoaderDialog}
                        />
                      </Suspense>
                    ) : null}
                    {isBuyNowDialogOpen && subscriptionQuery.isSuccess ? (
                      <Suspense fallback={null}>
                        <BuyNowDialog
                          close={closeBuyNowDialog}
                          is_expired={!!subscriptionQuery.data.expired}
                          subscription={subscriptionQuery.subscriptionClass}
                          has_billing_info={!!subscriptionQuery.data.has_billing_info}
                        />
                      </Suspense>
                    ) : null}
                    {subscriptionQuery.isSuccess ? (
                      <RedeemAccessToken
                        subscription={subscriptionQuery.subscriptionClass}
                        showDialog={isRedeemAccessTokenDialogOpen}
                        hideDialog={closeRedeemAccessTokenDialog}
                        allowInviteCode={subscriptionQuery.data.allowInviteCode}
                      />
                    ) : null}
                  </FluentProvider>
                </IdPrefixProvider>
                <LegacyUtils ref={legacyRef} />
              </A3DocumentContextProvider>
            </AzureRouter>
          </DataLoaderContextProvider>
        </MixPanelContext.Provider>
      </IntlProvider>
    </ThemeProvider>
  );
}

export default App;
